import { IconFont } from '@/components';
import { Upload, message } from 'antd';
import { RcFile, UploadFile, UploadProps } from 'antd/lib/upload';
import { FC, useEffect, useRef, useState } from 'react';
import { ACTION, OCR_ACCEPT, SIZE100MB, geneHeaders } from '../../const';
import { IUploadWithCustomAttrs } from '../../type';
import { getBase64 } from '../../utils';
import './index.less';

export type IProps = {
  module?: string;
  value?: UploadFile[];
  onChange?: (value: UploadFile[]) => void;
} & Omit<UploadProps, 'onChange'>;

const uploadProps: UploadProps = {
  multiple: true,
  showUploadList: false,
  action: ACTION,
};

/**
 * 430 图片这里只能上传图片，默认为['.jpg', '.jpeg', '.png']
 * OCR可以上传多个图片拼接成pdf
 */
export const ImageUploader: FC<IProps> = (props) => {
  const {
    value = [],
    accept: defaultAccept,
    onChange,
    module = '',
    headers = geneHeaders(),
    ...restProps
  } = props;
  const [fileList, setFileList] = useState<IUploadWithCustomAttrs[]>([]);
  const [accept, setAccept] = useState<string>('');
  const judgeImageSize = useRef<boolean>(false);
  const imageSizeMT100 = useRef<boolean>(false);
  const handledCount = useRef<number>(0);

  useEffect(() => {
    setFileList([...value]);
  }, [value]);

  useEffect(() => {
    if (defaultAccept) {
      setAccept(defaultAccept);
      return;
    }
    // 这里只能上传图片
    setAccept(OCR_ACCEPT);
  }, [module, defaultAccept]);

  const onRemove = (file: IUploadWithCustomAttrs) => {
    const list = fileList.filter((item) => item.uid !== file.uid);
    onChange?.([...list]);
  };

  const calFileSizes = (list: IUploadWithCustomAttrs[]) => {
    return list.reduce(
      (pre, cur) => pre + (cur && cur?.size ? cur?.size : 0),
      0,
    );
  };

  const reset = () => {
    judgeImageSize.current = false;
    imageSizeMT100.current = false;
    handledCount.current = 0;
  };

  const addHandledCount = (total: number) => {
    handledCount.current = handledCount.current + 1;
    if (handledCount.current === total) {
      reset();
    }
  };

  const beforeUpload = (file: RcFile, fileList: RcFile[]) => {
    if (!judgeImageSize.current) {
      judgeImageSize.current = true;
      imageSizeMT100.current = calFileSizes(value.concat(fileList)) > SIZE100MB;
      if (imageSizeMT100.current) {
        message.error('图片文件总大小不得超过100M');
        addHandledCount(fileList.length);
        return Upload.LIST_IGNORE;
      }
    } else {
      if (imageSizeMT100.current) {
        addHandledCount(fileList.length);
        return Upload.LIST_IGNORE;
      }
    }

    return new Promise<File>((resolve, reject) => {
      addHandledCount(fileList.length);
      const _file = file as RcFile & { _preview: string };
      const reader = getBase64(file);
      reader.onload = () => {
        _file._preview = reader.result as string;
        resolve(_file);
      };
      reader.onerror = () => reject();
    });
  };

  const onChangeFile = (value: {
    file: UploadFile;
    fileList: UploadFile[];
  }) => {
    const { file, fileList } = value;
    if (file.response && file.response.code !== 0) {
      message.error(file.response.msg);
    }
    onChange?.(fileList);
  };

  const clear = () => {
    setFileList([]);
    onChange?.([]);
    reset();
  };

  return (
    <div className="iu-wrapper">
      <div className="iu-wrapper__head">
        <p>已上传{fileList.length}张</p>
        <Upload
          {...uploadProps}
          {...restProps}
          headers={headers}
          accept={accept}
          fileList={fileList}
          onChange={onChangeFile}
          beforeUpload={beforeUpload}
        >
          <button type="button" className="iu-upload-btn" onClick={clear}>
            清空并重新上传
          </button>
        </Upload>
      </div>

      <div className="iu-wrapper__list">
        <div>
          <Upload
            {...uploadProps}
            {...restProps}
            accept={accept}
            fileList={fileList}
            beforeUpload={beforeUpload}
            onChange={onChangeFile}
          >
            <button type="button" className="iu-list__upload-btn">
              <IconFont
                type="icon-fangda"
                className="text-[#6F7176] text-base"
              ></IconFont>
              <p>继续添加</p>
            </button>
          </Upload>
        </div>

        {fileList.map((item) => {
          return (
            <div key={item.uid} className="iu-list__preview ">
              <IconFont
                type="icon-qingchuwenjian"
                className="iu-preview__delete-icon"
                onClick={() => onRemove(item)}
              ></IconFont>
              <div
                className="iu-preview__img"
                style={{
                  backgroundImage: `url("${item._preview || ''}")`,
                }}
              ></div>
              <p
                className={`iu-preview__name ${
                  item.error ? 'iu-preview__name--error' : ''
                }`}
              >
                {item.name}
              </p>
            </div>
          );
        })}
      </div>
    </div>
  );
};
